/**
 * Three Pillars Section Component
 * Showcases the three main service offerings with enhanced hover effects
 */

import React, { useState, useEffect, useRef } from 'react';
import { Section } from './Section';
import { SectionHeader } from './SectionHeader';
import { Users, HeadphonesIcon, Laptop, ArrowRight, CheckCircle, Sparkles } from 'lucide-react';

interface Pillar {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  benefits: string[];
  bestFor: string;
  ctaText: string;
  ctaLink: string;
  note: string;
  highlighted?: boolean;
  color: string;
}

const pillars: Pillar[] = [
  {
    icon: <Users className="w-8 h-8" />,
    title: 'OKR Consulting',
    subtitle: 'Build internal capability',
    description: 'We work hands-on with leadership and teams to:',
    benefits: [
      'Define outcome-driven objectives',
      'Design strong, controllable key results',
      'Align OKRs across company, teams, and individuals',
      'Integrate OKRs with KRAs, KPIs, and PMS',
      'Build effective review and learning cadences'
    ],
    note: 'This is not training. This is practical consulting anchored in your business context.',
    bestFor: 'Founder-led firms, leadership teams, transformation initiatives.',
    ctaText: 'Explore Consulting',
    ctaLink: '#contact',
    color: 'blue'
  },
  {
    icon: <HeadphonesIcon className="w-8 h-8" />,
    title: 'Managed OKR Services',
    subtitle: 'Momentum with external discipline',
    description: 'In this model, we:',
    benefits: [
      'Set up OKRs on the OKR Hub platform',
      'Facilitate OKR cycles and reviews',
      'Coach leaders and managers during execution',
      'Act as a neutral thinking partner',
      'Ensure rigor without bureaucracy'
    ],
    note: 'You execute. We ensure the system works consistently.',
    bestFor: 'Growing organizations, first-time OKR adopters, stretched leadership teams.',
    ctaText: 'Talk to an Advisor',
    ctaLink: '#contact',
    highlighted: true,
    color: 'primary'
  },
  {
    icon: <Laptop className="w-8 h-8" />,
    title: 'OKR Software',
    subtitle: 'Focused, clutter-free tool',
    description: 'The OKR Hub platform enables:',
    benefits: [
      'Clear OKR hierarchy (Company → Team → Individual)',
      'Simple check-ins and visibility',
      'Ownership-driven progress tracking',
      'Review-ready insights for leaders',
      'Reflection and learning—not just scoring'
    ],
    note: 'The tool supports thinking. It does not replace it.',
    bestFor: 'Teams ready to self-manage their OKR process.',
    ctaText: 'Request a Demo',
    ctaLink: '#contact',
    color: 'green'
  }
];

export const ThreePillarsSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Section id="services" background="white" padding="xl">
      <div ref={sectionRef}>
        <SectionHeader
          subtitle="Three Ways to Work with OKR Hub"
          title="Choose Your Path to Better Execution"
          description="We meet you where you are. Select the level of support that fits your organization's needs."
          align="center"
        />

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {pillars.map((pillar, index) => (
            <div
              key={index}
              className={`transition-all duration-700 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {pillar.highlighted ? (
                // Featured card with animated gradient border
                <div className="relative group h-full">
                  {/* Animated gradient border */}
                  <div className="absolute -inset-[2px] bg-gradient-to-r from-primary-400 via-primary-600 to-primary-400 rounded-3xl opacity-75 blur-[2px] group-hover:opacity-100 group-hover:blur-[3px] transition-all duration-500 animate-pulse"
                    style={{ backgroundSize: '200% 200%' }}
                  />

                  <div className="relative h-full bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 text-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl group-hover:shadow-primary-500/30 transition-all duration-500">
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-2xl" />

                    <div className="relative z-10">
                      {/* Badge */}
                      <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6">
                        <Sparkles className="w-4 h-4" />
                        <span>Most Popular</span>
                      </div>

                      {/* Icon */}
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                        {pillar.icon}
                      </div>

                      {/* Title */}
                      <h3 className="font-display text-2xl mb-2 leading-tight">
                        {pillar.title}
                      </h3>
                      <p className="text-white/80 text-lg mb-6 italic">
                        {pillar.subtitle}
                      </p>

                      {/* Description */}
                      <p className="font-medium mb-4 text-white/90">
                        {pillar.description}
                      </p>

                      {/* Benefits */}
                      <ul className="space-y-3 mb-6">
                        {pillar.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-white/90">{benefit}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Note */}
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6">
                        <p className="text-sm font-medium text-white">
                          {pillar.note}
                        </p>
                      </div>

                      {/* Best For */}
                      <div className="mb-6">
                        <div className="text-xs font-semibold uppercase tracking-wider mb-2 text-white/60">
                          Best For
                        </div>
                        <p className="text-sm text-white/80">
                          {pillar.bestFor}
                        </p>
                      </div>

                      {/* CTA */}
                      <a
                        href={pillar.ctaLink}
                        className="flex items-center justify-center gap-2 w-full bg-white text-primary-600 px-6 py-4 rounded-xl font-semibold hover:bg-white/90 hover:-translate-y-0.5 transition-all duration-300 shadow-lg"
                      >
                        {pillar.ctaText}
                        <ArrowRight className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                // Regular cards with hover lift
                <div className="group h-full bg-white rounded-2xl p-8 border-2 border-stone-200 hover:border-primary-300 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-pointer">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 ${
                    pillar.color === 'blue'
                      ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-500 group-hover:text-white'
                      : 'bg-green-100 text-green-600 group-hover:bg-green-500 group-hover:text-white'
                  } group-hover:scale-110 group-hover:rotate-3`}>
                    {pillar.icon}
                  </div>

                  {/* Title */}
                  <h3 className="font-display text-2xl text-ink mb-2 leading-tight group-hover:text-primary-600 transition-colors">
                    {pillar.title}
                  </h3>
                  <p className="text-ink-muted text-lg mb-6 italic">
                    {pillar.subtitle}
                  </p>

                  {/* Description */}
                  <p className="font-medium text-ink mb-4">
                    {pillar.description}
                  </p>

                  {/* Benefits */}
                  <ul className="space-y-3 mb-6">
                    {pillar.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 transition-colors ${
                          pillar.color === 'blue'
                            ? 'text-blue-500'
                            : 'text-green-500'
                        }`} />
                        <span className="text-sm text-ink-light">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Note */}
                  <div className={`rounded-xl p-4 mb-6 transition-colors ${
                    pillar.color === 'blue'
                      ? 'bg-blue-50 border border-blue-200 group-hover:bg-blue-100'
                      : 'bg-green-50 border border-green-200 group-hover:bg-green-100'
                  }`}>
                    <p className="text-sm font-medium text-ink">
                      {pillar.note}
                    </p>
                  </div>

                  {/* Best For */}
                  <div className="mb-6">
                    <div className="text-xs font-semibold uppercase tracking-wider mb-2 text-ink-muted">
                      Best For
                    </div>
                    <p className="text-sm text-ink-light">
                      {pillar.bestFor}
                    </p>
                  </div>

                  {/* CTA */}
                  <a
                    href={pillar.ctaLink}
                    className={`flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl font-semibold transition-all duration-300 ${
                      pillar.color === 'blue'
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    } hover:-translate-y-0.5 shadow-lg hover:shadow-xl`}
                  >
                    {pillar.ctaText}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className={`mt-16 text-center max-w-3xl mx-auto transition-all duration-700 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-xl text-ink-light leading-relaxed">
            Not sure which option is right for you?{' '}
            <a
              href="#contact"
              className="text-primary-600 font-semibold hover:text-primary-700 underline decoration-2 underline-offset-4 hover:decoration-primary-400 transition-colors"
            >
              Let's have a conversation
            </a>{' '}
            and find the best fit for your organization's needs.
          </p>
        </div>
      </div>
    </Section>
  );
};
